import { BaseAnalyticsService } from "@/modules/analytics/services/base-analytics.service";
import { DecodedToken } from "@/modules/common/context/auth/auth.context";
import { EnvironmentVariables } from "@/modules/common/types";

declare global {
  interface Window {
    Moengage?: {
      track_event: (name: string, payload?: Record<string, unknown>) => void;
      logout: () => void;
      identifyUser: (userId: string) => Promise<void>;
      add_email: (email: string) => void;
      add_user_attribute: (key: string, value: string) => void;
      add_mobile: (phone: string) => void;
      add_last_name: (name: string) => void;
      add_first_name: (name: string) => void;
      add_user_name: (name: string) => void;
      destroy_session: () => void;
    };
    moe?: (opts: {
      app_id: string;
      debug_logs?: number;
      enableSPA?: boolean;
    }) => any;
    moeDataCenter?: string;
  }
}

export class MoEngageAnalyticsService extends BaseAnalyticsService {
  private readonly appId = EnvironmentVariables.MOENGAGE_APP_ID?.toString();
  private readonly dc = EnvironmentVariables.MOENGAGE_DC?.toString();
  private readonly sdkVersion = "2";
  public static readonly DEBUG_MODE =
    EnvironmentVariables.MOENGAGE_APP_ID?.toString().includes("_DEBUG") ? 1 : 0;

  public async init(): Promise<void> {
    if (window.Moengage) return;

    if (!this.appId || !this.dc) {
      console.error("MoEngage APP ID or Data Center is missing");
      return;
    }

    window.moeDataCenter = this.dc;

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://cdn.moengage.com/release/${this.dc}/versions/${this.sdkVersion}/moe_webSdk.min.latest.js`;
      script.async = true;

      script.onload = () => {
        try {
          const sdkInstance = window.moe?.({
            app_id: this.appId,
            debug_logs: MoEngageAnalyticsService.DEBUG_MODE,
            enableSPA: true,
          });
          window.Moengage = sdkInstance;
          resolve();
        } catch (e) {
          console.error("MoEngage init error", e);
          reject(e);
        }
      };

      script.onerror = () => {
        console.error("Failed to load MoEngage SDK");
        reject(new Error("MoEngage SDK load failed"));
      };

      document.head.appendChild(script);
    });
  }

  public identify(user: DecodedToken): void {
    if (!window.Moengage) return;

    window?.Moengage?.identifyUser(user["cognito:username"])
      .then(() => {
        this.setUserAttributes({
          email: user?.email,
          firstName: user?.name,
          userName: user?.email?.split("@")[0],
          phone: user?.phone_number,
        });
      })
      .catch((e) => {
        console.error("MoEngage identify error", e);
      });
  }

  public setUserAttributes(attributes: Record<string, unknown>): void {
    if (!window.Moengage) return;

    if (attributes.email) {
      window.Moengage?.add_email(attributes.email as string);
    }
    if (attributes.firstName) {
      window.Moengage?.add_first_name(attributes.firstName as string);
    }
    if (attributes.lastName) {
      window.Moengage?.add_last_name(attributes.lastName as string);
    }
    if (attributes.userName) {
      window.Moengage?.add_user_name(attributes.userName as string);
    }
    if (attributes.phone) {
      window.Moengage?.add_mobile(attributes.phone as string);
    }
  }

  public pageView(event: string, payload?: Record<string, unknown>): void {
    window.Moengage?.track_event(event, payload);
  }

  public track(event: string, payload: Record<string, unknown> = {}): void {
    window.Moengage?.track_event(event, payload);
  }

  public unidentify(): void {
    window.Moengage?.destroy_session();
  }
}
